import { useMemo, useState } from 'react'
import { serviceTotal, tax } from '../../AddServiceForm/typings'
import { currencyString } from '@sales-center/common'
import { ENTITY_TYPES } from '@sales-center/common'
import { useAppSelector } from '@user/store/hooks'
import { selectLeadDetail } from '@user/store/lead'
import { selectOpportunityDetail } from '@user/store/opportunity'
import { DialogState, ServiceCardProps } from '../typings'
import { useSchedulingEnabled } from '@user/store/scheduling'

export function useData(
  props: Pick<ServiceCardProps, 'service' | 'index' | 'entityType'>,
) {
  const { service, index, entityType } = props

  const {
    quantity,
    initialPrice,
    initialDiscountAmount,
    initialDiscountType,
    recurringPrice,
    recurringDiscountAmount,
    recurringDiscountType,
    taxRate,
    annualOccurrences,
    appointmentEntityId,
    estimateEntityId,
    gstHstRate,
    pstQstRate,
  } = service

  const serviceNumber = index + 1

  const [dialog, setDialog] = useState<DialogState>()

  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const initialDiscount = useMemo(
    () =>
      initialDiscountType === 'dollar'
        ? `$${currencyString(initialDiscountAmount)}`
        : `${initialDiscountAmount * 100}%`,
    [initialDiscountAmount, initialDiscountType],
  )

  const recurringDiscount = useMemo(
    () =>
      recurringDiscountType === 'dollar'
        ? `$${currencyString(recurringDiscountAmount)}`
        : `${recurringDiscountAmount * 100}%`,
    [recurringDiscountAmount, recurringDiscountType],
  )

  const total = useMemo(() => {
    return serviceTotal(
      initialPrice -
        (initialDiscountType === 'dollar'
          ? initialDiscountAmount
          : initialPrice * initialDiscountAmount),
      recurringPrice -
        (recurringDiscountType === 'dollar'
          ? recurringDiscountAmount
          : recurringPrice * recurringDiscountAmount),
      annualOccurrences,
      quantity,
    )
  }, [
    annualOccurrences,
    initialPrice,
    quantity,
    recurringPrice,
    initialDiscountType,
    initialDiscountAmount,
    recurringDiscountType,
    recurringDiscountAmount,
  ])

  const totalTax = useMemo(() => {
    return `$${currencyString(tax(total, taxRate))}`
  }, [total, taxRate])

  // Canadian Tax calculation for GST & PST
  const gstTaxTotal = useMemo(() => {
    const gstTax = tax(total, gstHstRate)
    return gstTax > 0 ? `$${currencyString(gstTax)}` : ''
  }, [total, gstHstRate])

  const pstTaxTotal = useMemo(() => {
    const pstTax = tax(total, pstQstRate)
    return pstTax > 0 ? `$${currencyString(pstTax)}` : ''
  }, [total, pstQstRate])

  const schedulingEnabled = useSchedulingEnabled()

  const showApptWarning =
    (estimateEntityId !== null || appointmentEntityId !== null) &&
    schedulingEnabled

  return {
    dialog,
    totalTax,
    setDialog,
    serviceNumber,
    initialDiscount,
    showApptWarning,
    recurringDiscount,
    detailId: detail?.id,
    gstTaxTotal,
    pstTaxTotal,
  }
}
